// src/pages/PeriodSnapshotsPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { apiGet, apiPost } from "../utils/apiClient";
import LoadingInline from "../components/LoadingInline";
import "./TableCommon.css";
import "./PeriodSnapshotsPage.css";

import { useUserContext } from "../contexts/UserContext";
import { isFreePlanUser, isLightPlanUser} from "../utils/plan";
import { openUpgradeForm } from "../components/FreePlanUpgradeDialog";
import { downloadCsv, type CsvCol } from "../utils/csvExport";

import {
    COLUMN_DEFS,
    HEADER_STRUCTURE,
    type HeaderSection,
    type MetricKey,
    type HeaderSubGroupDef,
} from "./DailyProfitColumns";

/* ===============================
   util
================================ */
const fmtNum = (v?: number | null) =>
    v == null ? "" : Math.round(v).toLocaleString("ja-JP");

const fmtAvg = (v?: number | null) =>
    v == null ? "" : v.toFixed(1);

const fmtRate = (v?: number | null) =>
    v == null ? "" : (v * 100).toFixed(1) + "%";

/* ===============================
   BQ日付吸収
================================ */
type BqValue = string | { value: string } | null | undefined;

const pickValue = (v: BqValue) =>
    typeof v === "string"
        ? v
        : v && typeof v === "object" && "value" in v
            ? v.value
            : "";

const fmtMd = (v: BqValue) => {
    const d = dayjs(pickValue(v));
    return d.isValid() ? d.format("M/D") : "";
};

const fmtYmd = (v: BqValue) => {
    const d = dayjs(pickValue(v));
    return d.isValid() ? d.format("YYYY/MM/DD") : "";
};

/* ===============================
   型
================================ */
type SnapshotRow = {
    snapshot_id: string;
    memo?: string | null;
    start_date: BqValue;
    end_date: BqValue;
    period_days?: number | null;
    created_at?: BqValue;
} & Partial<Record<MetricKey, number | null>>;

/* ===============================
   型ガード
================================ */
function hasSub(
    sec: HeaderSection
): sec is Extract<HeaderSection, { sub: readonly HeaderSubGroupDef[] }> {
    return "sub" in sec;
}
function isRowSpan2(
    sec: HeaderSection
): sec is Extract<HeaderSection, { rowSpan2: true }> {
    return "rowSpan2" in sec && sec.rowSpan2 === true;
}

const STORAGE_KEY = "periodSnapshots.visibleColumns.v1";

/* ===============================
   APIエラー処理
================================ */
async function readJsonSafe(res: Response) {
    try {
        return await res.json();
    } catch {
        return null;
    }
}

async function ensureOk(res: Response) {
    const json = await readJsonSafe(res);

    if (res.ok) return json;

    const err = new Error(
        json?.message ||
        json?.detail ||
        json?.error ||
        `HTTP ${res.status}`
    ) as Error & {
        status?: number;
        body?: any;
    };

    err.status = res.status;
    err.body = json;
    throw err;
}

function getErrorMessage(err: any) {
    return (
        err?.body?.message ||
        err?.body?.detail ||
        err?.message ||
        "エラーが発生しました。"
    );
}

/* ===============================
   フリープラン ダイアログ
================================ */
const FreePlanUpgradeDialog = ({
    open,
    onClose,
}: {
    open: boolean;
    onClose: () => void;
}) => {
    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/45 px-4">
            <div className="w-full max-w-[480px] overflow-hidden rounded-xl bg-white shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
                <div className="bg-[#4a72c4] px-7 pb-5 pt-6">
                    <div className="mb-2 text-[11px] font-medium tracking-[0.08em] text-[#cfdcf5]">
                        スタンダードプラン以上の機能
                    </div>
                    <div className="text-[17px] font-semibold leading-[1.5] text-white">
                        期間利益はスタンダードプラン以上で
                        <br />
                        ご利用いただけます
                    </div>
                </div>

                <div className="px-7 py-6">
                    <div className="mb-[18px] grid grid-cols-1 gap-[10px] sm:grid-cols-2">
                        <div className="rounded-lg bg-[#f0f4fb] px-4 py-[14px]">
                            <div className="mb-1 text-[12px] font-semibold text-[#3a5aa0]">
                                期間を指定して利益を比較
                            </div>
                            <div className="text-[12px] leading-[1.5] text-[#555]">
                                任意の期間を自由に設定して利益を集計できます
                            </div>
                        </div>

                        <div className="rounded-lg bg-[#f0f4fb] px-4 py-[14px]">
                            <div className="mb-1 text-[12px] font-semibold text-[#3a5aa0]">
                                施策の効果を利益で検証
                            </div>
                            <div className="text-[12px] leading-[1.5] text-[#555]">
                                ポイント施策・イベントの手残りを数字で確認
                            </div>
                        </div>
                    </div>

                    <p className="mb-5 text-[13px] leading-[1.7] text-[#555]">
                        「ポイント10倍デーは本当に儲かったのか？」売上ではなく
                        <strong className="font-semibold text-[#333]">手残り利益</strong>
                        で判断したいときに活躍します。
                    </p>

                    <div className="flex gap-[10px] border-t border-[#eee] pt-4">
                        <button
                            className="flex-1 rounded-lg bg-[#4a72c4] px-4 py-[11px] text-[14px] font-semibold text-white transition hover:bg-[#3a5fad]"
                            onClick={() => openUpgradeForm("期間指定の利益集計")}
                        >
                            プランをアップグレードする →
                        </button>

                        <button
                            className="whitespace-nowrap rounded-lg border border-[#ddd] bg-white px-4 py-[11px] text-[13px] text-[#888] transition hover:bg-[#f5f5f5]"
                            onClick={onClose}
                        >
                            閉じる
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ===============================
   共通テーブル
================================ */
const SnapshotTable = ({
    title,
    rows,
    isAverage,
    visibleCols,
    onEdit,
    csvFilePrefix,
}: {
    title: string;
    rows: SnapshotRow[];
    isAverage: boolean;
    visibleCols: Record<string, boolean>;
    onEdit?: (row: SnapshotRow) => void;
    /** RN-190: 指定時のみ CSVエクスポートボタンを表示（ファイル名の先頭） */
    csvFilePrefix?: string;
}) => {
    // RN-303: 楽天外広告費①〜③は、日次利益と同じく「どれかの期間に名前か金額があるときだけ」出し、見出しを名前にする。
    // 期間ごとに名前が違うときは、共通の見出し（楽天外広告①など）のまま
    const adInfo = useMemo(() => {
        const info: Record<string, { shown: boolean; name: string | null }> = {};
        for (let i = 1; i <= 3; i++) {
            const names = new Set<string>();
            let hasCost = false;
            for (const r of rows as any[]) {
                const n = r?.[`non_rakuten_ad_${i}_name`];
                if (n) names.add(String(n));
                const c = Number(r?.[`non_rakuten_ad_${i}_cost`]);
                if (Number.isFinite(c) && c !== 0) hasCost = true;
            }
            info[`non_rakuten_ad_${i}`] = {
                shown: names.size > 0 || hasCost,
                name: names.size === 1 ? Array.from(names)[0] : null,
            };
        }
        return info;
    }, [rows]);
    const adSlotOf = (key: string) => key.match(/^(non_rakuten_ad_\d+)_/)?.[1] ?? null;
    const isVisible = (key: MetricKey) => {
        const slot = adSlotOf(key as string);
        if (slot && !adInfo[slot]?.shown) return false;
        return visibleCols[key as string] !== false;
    };
    const colTitle = (c: (typeof COLUMN_DEFS)[number]) => {
        const m = (c.key as string).match(/^(non_rakuten_ad_\d+)_cost$/);
        if (m && adInfo[m[1]]?.name) return adInfo[m[1]].name as string;
        return c.title;
    };

    // RN-190: 表示中の列だけを出力（列の並び・見出しは画面と同じ。% は画面と同じく小数1桁）
    const onExportCsv = () => {
        if (!csvFilePrefix || rows.length === 0) return;

        const cols: CsvCol<Record<string, any>>[] = [
            { key: "period", header: "期間", type: "text" },
            { key: "memo", header: "メモ", type: "text" },
            { key: "created_at", header: "作成日", type: "text" },
            ...COLUMN_DEFS.filter((c) => isVisible(c.key as MetricKey)).map((c) => ({
                key: c.key as string,
                header: `${c.group}_${colTitle(c)}`,
                type: (c.type === "rate" ? "rate" : "num") as "rate" | "num",
            })),
        ];

        // 平均値の表は、画面（fmtAvg）と同じく小数1桁にそろえる。% の列は csvExport 側で小数1桁になる
        const roundAvg = (r: SnapshotRow): Record<string, any> => {
            if (!isAverage) return r;
            const out: Record<string, any> = { ...r };
            for (const c of COLUMN_DEFS) {
                if (c.type === "rate") continue;
                const v = r[c.key as MetricKey];
                if (v == null) continue;
                const n = Number(v);
                if (Number.isFinite(n)) out[c.key as string] = n.toFixed(1);
            }
            return out;
        };

        const exportRows = rows.map((r) => ({
            ...roundAvg(r),
            period:
                `${fmtYmd(r.start_date)}～${fmtYmd(r.end_date)}` +
                (r.period_days ? ` (${r.period_days}日)` : ""),
            memo: r.memo ?? "",
            created_at: r.created_at ? fmtYmd(r.created_at) : "",
        }));

        downloadCsv(`${csvFilePrefix}_${dayjs().format("YYYYMMDD")}.csv`, exportRows, cols, {
            withBom: true,
            newline: "\r\n",
            rateMode: "0-1",
            rateDigits: 1,
        });
    };

    const countVisibleByGroups = (groups: readonly string[]) =>
        COLUMN_DEFS.filter(
            (c) => groups.includes(c.group) && isVisible(c.key as MetricKey)
        ).length;

    const headerComputed = useMemo(() => {
        const top: Array<{ sec: HeaderSection; colSpan: number }> = [];

        for (const sec of HEADER_STRUCTURE) {
            const colSpan = hasSub(sec)
                ? sec.sub.reduce(
                    (sum, s) => sum + countVisibleByGroups(s.groups),
                    0
                )
                : countVisibleByGroups(sec.groups);

            if (colSpan > 0) top.push({ sec, colSpan });
        }

        const sub: Array<{ top: string; name: string; colSpan: number }> = [];
        for (const { sec } of top) {
            if (!hasSub(sec)) continue;
            for (const s of sec.sub) {
                const colSpan = countVisibleByGroups(s.groups);
                if (colSpan > 0) sub.push({ top: sec.top, name: s.name, colSpan });
            }
        }

        const visibleMetricCount = COLUMN_DEFS.filter((c) =>
            isVisible(c.key as MetricKey)
        ).length;

        return { top, sub, tableColCount: 2 + visibleMetricCount };
    }, [visibleCols, adInfo]);

    return (
        <div className="mb-6">
            <div className="mb-1 flex items-center justify-between gap-2">
                <div className="text-sm font-semibold">{title}</div>
                {csvFilePrefix && (
                    <button
                        type="button"
                        className="text-sm border rounded px-2 py-1 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={onExportCsv}
                        disabled={rows.length === 0}
                    >
                        CSVエクスポート
                    </button>
                )}
            </div>
            <p className="mb-1 text-[11px] text-slate-400">
                ← 横にスクロールすると他の指標を表示できます →
            </p>

            <div className="dp-table-wrapper">
                <table className="dp-table">
                    <thead>
                        <tr>
                            <th
                                rowSpan={2}
                                colSpan={2}
                                className="dp-title-cell dp-title-sticky"
                            >
                                期間集計
                            </th>
                            {headerComputed.top.map(({ sec, colSpan }) => (
                                <th
                                    key={sec.top}
                                    colSpan={colSpan}
                                    rowSpan={isRowSpan2(sec) ? 2 : 1}
                                    className="dp-group-header"
                                >
                                    {sec.top}
                                </th>
                            ))}
                        </tr>

                        <tr>
                            {headerComputed.sub.map((s) => (
                                <th
                                    key={`${s.top}-${s.name}`}
                                    colSpan={s.colSpan}
                                    className="dp-subgroup-header"
                                >
                                    {s.name}
                                </th>
                            ))}
                        </tr>

                        <tr>
                            <th className="dp-col-header sticky-left-1 text-center">期間</th>
                            <th className="dp-col-header sticky-left-2">メモ</th>

                            {COLUMN_DEFS.map((c) =>
                                isVisible(c.key as MetricKey) ? (
                                    <th key={c.key as string} className="dp-col-header">
                                        {colTitle(c)}
                                    </th>
                                ) : null
                            )}
                        </tr>
                    </thead>

                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td
                                    colSpan={headerComputed.tableColCount}
                                    className="p-4 text-center text-slate-500"
                                >
                                    データなし
                                </td>
                            </tr>
                        )}

                        {rows.map((r) => {
                            const period =
                                `${fmtYmd(r.start_date)}～${fmtYmd(r.end_date)}` +
                                (r.period_days ? ` (${r.period_days}日)` : "");

                            return (
                                <tr
                                    key={r.snapshot_id}
                                    className={onEdit ? "cursor-pointer hover:bg-slate-50" : ""}
                                    onClick={() => onEdit?.(r)}
                                >
                                    <td className="dp-cell sticky-left-1">{period}</td>
                                    <td className="dp-cell sticky-left-2">
                                        <div className="w-full text-left">
                                            {r.memo && (
                                                <div className="max-w-[240px] truncate" title={r.memo}>
                                                    {r.memo}
                                                </div>
                                            )}

                                            {r.created_at && (
                                                <div className="text-[10px] text-slate-400">
                                                    {fmtYmd(r.created_at)}
                                                </div>
                                            )}
                                        </div>
                                    </td>

                                    {COLUMN_DEFS.map((c) => {
                                        if (!isVisible(c.key as MetricKey)) return null;
                                        const raw = r[c.key as MetricKey] ?? null;
                                        const content =
                                            c.type === "rate"
                                                ? fmtRate(raw)
                                                : isAverage
                                                    ? fmtAvg(raw)
                                                    : fmtNum(raw);

                                        return (
                                            <td key={c.key as string} className="dp-cell num">
                                                {content}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

/* ===============================
   Page
================================ */
const PeriodSnapshotsPage: React.FC = () => {
    const user = useUserContext();

    const [totalRows, setTotalRows] = useState<SnapshotRow[]>([]);
    const [avgRows, setAvgRows] = useState<SnapshotRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [apiMessage, setApiMessage] = useState("");
    // RN-190: 作成・更新・削除の成功時に表示する（エラー用の apiMessage とは別枠）
    const [successMessage, setSuccessMessage] = useState("");

    const [startDate, setStartDate] = useState(
        dayjs().subtract(7, "day").format("YYYY-MM-DD")
    );
    const [endDate, setEndDate] = useState(
        dayjs().subtract(1, "day").format("YYYY-MM-DD")
    );
    const [memo, setMemo] = useState("");
    const [editing, setEditing] = useState<SnapshotRow | null>(null);

    const [visibleCols, setVisibleCols] = useState<Record<string, boolean>>({});
    const [showFreePlanDialog, setShowFreePlanDialog] = useState(false);

    useEffect(() => {
        let parsed: Record<string, boolean> | null = null;
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) parsed = JSON.parse(raw);
        } catch {
            parsed = null;
        }

        if (parsed && typeof parsed === "object") {
            setVisibleCols(parsed);
        } else {
            const init: Record<string, boolean> = {};
            COLUMN_DEFS.forEach((c) => (init[c.key as string] = true));
            setVisibleCols(init);
        }
    }, []);

    const isVisible = (key: MetricKey) => visibleCols[key as string] !== false;

    const toggleColumn = (key: MetricKey) => {
        setVisibleCols((prev) => {
            const next = { ...prev, [key as string]: !(prev[key as string] ?? true) };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
            return next;
        });
    };

    const setAllColumns = (value: boolean) => {
        const next: Record<string, boolean> = {};
        COLUMN_DEFS.forEach((c) => (next[c.key as string] = value));
        setVisibleCols(next);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    };

    const groups = Array.from(new Set(COLUMN_DEFS.map((c) => c.group))).map(
        (g) => ({
            name: g,
            cols: COLUMN_DEFS.filter((c) => c.group === g),
        })
    );

    const isRestrictedPlan = useMemo(() => isFreePlanUser(user) || isLightPlanUser(user), [user]);

    const load = async () => {
        setLoading(true);
        try {
            const res = await apiGet("/api/period-snapshots");
            const json = await ensureOk(res);
            setTotalRows(json?.total ?? []);
            setAvgRows(json?.avg ?? []);
        } catch (err: any) {
            setApiMessage(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isRestrictedPlan) {
            setLoading(false);
            setShowFreePlanDialog(true);
            return;
        }
        load();
    }, [isRestrictedPlan]);

    const startEdit = (r: SnapshotRow) => {
        if (isRestrictedPlan) return;
        setApiMessage("");
        setSuccessMessage("");
        setEditing(r);
        setStartDate(dayjs(pickValue(r.start_date)).format("YYYY-MM-DD"));
        setEndDate(dayjs(pickValue(r.end_date)).format("YYYY-MM-DD"));
        setMemo(r.memo ?? "");
    };

    const freeMinStart = useMemo(
        () => dayjs().subtract(1, "month").startOf("month"),
        []
    );
    const freeMinStartDate = useMemo(
        () => freeMinStart.format("YYYY-MM-DD"),
        [freeMinStart]
    );
    const todayYmd = useMemo(() => dayjs().format("YYYY-MM-DD"), []);

    const dateWarning = useMemo(() => {
        const s = dayjs(startDate);
        const e = dayjs(endDate);

        if (!s.isValid() || !e.isValid()) return "開始日・終了日を正しく入力してください。";
        if (e.isBefore(s, "day")) return "終了日は開始日以降にしてください。";

        if (isRestrictedPlan) {
            if (s.isBefore(freeMinStart, "day")) {
                return `フリープランでは開始日は ${freeMinStartDate} 以降（今月・先月のみ）にしてください。`;
            }
        }

        if (e.isAfter(dayjs(), "day")) return "終了日は今日以前にしてください。";

        return "";
    }, [startDate, endDate, isRestrictedPlan, freeMinStart, freeMinStartDate]);

    const isSubmitDisabled = isRestrictedPlan || !!dateWarning;

    async function createSnapshot() {
        if (isSubmitDisabled) return;
        if (!window.confirm(`${startDate} ～ ${endDate} で集計しますか？`)) return;

        setLoading(true);
        setApiMessage("");
        setSuccessMessage("");
        try {
            const res = await apiPost("/api/period-snapshots", { startDate, endDate, memo });
            await ensureOk(res);
            setSuccessMessage(`集計しました（${startDate} ～ ${endDate}）`);
            setMemo("");
            await load();
        } catch (err: any) {
            if (err?.status === 409) {
                setApiMessage(
                    getErrorMessage(err) ||
                    "直前に作成・更新したデータは、反映待ちのためしばらく操作できません。少し待ってから再試行してください。"
                );
            } else {
                setApiMessage(getErrorMessage(err));
            }
        } finally {
            setLoading(false);
        }
    }

    async function updateSnapshot() {
        if (isSubmitDisabled) return;
        if (!editing) return;
        if (!window.confirm(`${startDate} ～ ${endDate} に変更して再集計しますか？`)) return;

        setLoading(true);
        setApiMessage("");
        setSuccessMessage("");
        try {
            const res = await apiPost("/api/period-snapshots/update", {
                snapshotId: editing.snapshot_id,
                startDate,
                endDate,
                memo,
            });
            await ensureOk(res);
            setSuccessMessage(`再集計しました（${startDate} ～ ${endDate}）`);
            setEditing(null);
            setMemo("");
            await load();
        } catch (err: any) {
            if (err?.status === 409) {
                setApiMessage(
                    getErrorMessage(err) ||
                    "作成直後のスナップショットは、BigQuery反映待ち中のため更新できません。少し待ってから再試行してください。"
                );
            } else {
                setApiMessage(getErrorMessage(err));
            }
        } finally {
            setLoading(false);
        }
    }

    async function deleteSnapshot() {
        if (isRestrictedPlan) return;
        if (!editing) return;

        if (
            !window.confirm(
                "この期間集計を削除します。\n※累計・平均の両方が消えます。本当にいいですか？"
            )
        ) {
            return;
        }

        setLoading(true);
        setApiMessage("");
        setSuccessMessage("");
        try {
            const res = await apiPost("/api/period-snapshots/delete", {
                snapshotId: editing.snapshot_id,
            });
            await ensureOk(res);
            setSuccessMessage("削除しました");
            setEditing(null);
            setMemo("");
            await load();
        } catch (err: any) {
            if (err?.status === 409) {
                setApiMessage(
                    getErrorMessage(err) ||
                    "作成直後のスナップショットは、BigQuery反映待ち中のため削除できません。数分後に再試行してください。"
                );
            } else {
                setApiMessage(getErrorMessage(err));
            }
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="w-pl relative flex flex-col gap-4">
            {loading && (
                <div className="fixed bottom-0 left-0 right-0 top-14 z-50 flex items-center justify-center bg-white/0 md:left-[210px]">
                    <LoadingInline />
                </div>
            )}

            <FreePlanUpgradeDialog
                open={showFreePlanDialog}
                onClose={() => setShowFreePlanDialog(false)}
            />

            {!!successMessage && (
                <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                    {successMessage}
                </div>
            )}

            {!!apiMessage && (
                <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    {apiMessage}
                </div>
            )}

            <div className={isRestrictedPlan ? "pointer-events-none select-none opacity-50" : ""}>
                <div className="flex flex-col gap-2 rounded border bg-white p-3">
                    <div className="flex items-end gap-2">
                        <div>
                            <div className="text-xs text-slate-500">開始日</div>
                            <input
                                type="date"
                                className="rounded border px-2 py-1 text-sm"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                min={isRestrictedPlan ? freeMinStartDate : undefined}
                                max={todayYmd}
                            />
                        </div>
                        <div>
                            <div className="text-xs text-slate-500">終了日</div>
                            <input
                                type="date"
                                className="rounded border px-2 py-1 text-sm"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                max={todayYmd}
                            />
                        </div>
                        <div className="flex-1">
                            <div className="text-xs text-slate-500">メモ</div>
                            <input
                                type="text"
                                className="w-full rounded border px-2 py-1 text-sm"
                                value={memo}
                                onChange={(e) => setMemo(e.target.value)}
                            />
                        </div>

                        {!editing ? (
                            <button
                                className="rounded bg-blue-600 px-4 py-1.5 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
                                onClick={createSnapshot}
                                disabled={isSubmitDisabled || loading}
                                title={dateWarning || ""}
                            >
                                集計する
                            </button>
                        ) : (
                            <>
                                <button
                                    className="rounded bg-emerald-600 px-4 py-1.5 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
                                    onClick={updateSnapshot}
                                    disabled={isSubmitDisabled || loading}
                                    title={dateWarning || ""}
                                >
                                    変更して再集計
                                </button>

                                <button
                                    className="rounded bg-red-600 px-4 py-1.5 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
                                    onClick={deleteSnapshot}
                                    disabled={loading}
                                >
                                    削除
                                </button>

                                <button
                                    className="rounded border px-3 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                                    onClick={() => {
                                        setEditing(null);
                                        setMemo("");
                                        setApiMessage("");
                                        setSuccessMessage("");
                                    }}
                                    disabled={loading}
                                >
                                    キャンセル
                                </button>
                            </>
                        )}
                    </div>

                    {dateWarning && (
                        <div className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700">
                            {dateWarning}
                        </div>
                    )}

                    {isRestrictedPlan && !dateWarning && (
                        <div className="text-[11px] text-slate-500">
                            フリープランでは「今月・先月」の範囲で集計できます（開始日：{freeMinStartDate}以降）。
                        </div>
                    )}
                </div>

                <details className="mt-4 inline-block self-start rounded-md border bg-white p-2 text-sm">
                    <summary className="cursor-pointer select-none text-blue-600">
                        列表示設定
                    </summary>

                    <div className="mt-2 flex flex-col gap-2">
                        <div className="flex gap-2">
                            <button
                                className="rounded border px-2 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                                onClick={() => setAllColumns(true)}
                                disabled={loading}
                            >
                                全部ON
                            </button>
                            <button
                                className="rounded border px-2 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                                onClick={() => setAllColumns(false)}
                                disabled={loading}
                            >
                                全部OFF
                            </button>
                        </div>

                        <div className="max-h-64 overflow-y-auto pr-1">
                            {groups.map((g) => (
                                <div key={g.name} className="mb-2">
                                    <div className="mb-1 text-xs font-semibold">{g.name}</div>
                                    <div className="flex flex-wrap gap-2">
                                        {g.cols.map((c) => {
                                            const key = c.key as MetricKey;
                                            return (
                                                <label
                                                    key={c.key as string}
                                                    className="flex items-center gap-1 rounded border bg-slate-50 px-1 py-0.5 text-xs"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isVisible(key)}
                                                        onChange={() => toggleColumn(key)}
                                                        disabled={loading}
                                                    />
                                                    <span>{c.title}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </details>

                <div className="mt-4">
                    <SnapshotTable
                        title="期間累計"
                        rows={totalRows}
                        isAverage={false}
                        visibleCols={visibleCols}
                        onEdit={startEdit}
                        csvFilePrefix="period_profit_total"
                    />
                </div>

                <SnapshotTable
                    title="期間平均値（1日あたり）"
                    rows={avgRows}
                    isAverage={true}
                    visibleCols={visibleCols}
                    csvFilePrefix="period_profit_avg"
                />
            </div>
        </div>
    );
};

export default PeriodSnapshotsPage;